/**
 * CLIKKER — Public Creator Storefront Checkout
 * Asset: clikker-store-checkout.js
 * Version: 1.1.0 — backend-owned order creation + direct Shopify/Razorpay checkout
 *
 * Requires:
 * - Firebase compat SDK
 * - clikker-firebase.js
 * - clikker-product-engine.js
 * - clikker-product-workflows.js
 * - clikker-store.js v9.0.0+
 *
 * Owns:
 * - Product detail modal
 * - Buyer requirement fields
 * - Buyer field validation
 * - Secure checkout request preparation
 * - Backend-owned Firestore order + Shopify checkout creation
 * - Shopify / Razorpay redirect
 * - Checkout lock/recovery state
 */
(function () {
  "use strict";

  var VERSION = "1.1.0";
  var REQUIRED_STORE_CORE_VERSION = "9.0.0";
  var PAYMENT_TIMEOUT_MS = 30000;
  var STORE_API_TIMEOUT_MS = 15000;
  var SHOPIFY_CHECKOUT_FUNCTION_URL =
    "https://asia-south1-creator-dashboard-7273e.cloudfunctions.net/createClikkerCheckout";

  var CheckoutAPI = window.ClikkerStoreCheckout = window.ClikkerStoreCheckout || {};
  var modal = document.getElementById("cpsProductModal");

  var checkoutInProgress = false;
  var activeCheckoutKey = "";
  var lastFocusedElement = null;
  var modalKeyHandler = null;
  var activeProductId = "";

  if (!modal) {
    console.error("[Clikker Checkout] Required #cpsProductModal container is missing.");
    return;
  }

  /* ========================================================================
     STORE CORE ACCESS
     ======================================================================== */

  function StoreAPI() {
    return window.ClikkerStore || {};
  }

  function StoreUtils() {
    return StoreAPI().utils || {};
  }

  async function waitForStoreAPI() {
    var started = Date.now();

    while (Date.now() - started < STORE_API_TIMEOUT_MS) {
      var store = StoreAPI();

      if (
        store &&
        typeof store.getProductById === "function" &&
        typeof store.getStore === "function" &&
        typeof store.getProducts === "function"
      ) {
        return store;
      }

      await new Promise(function (resolve) {
        setTimeout(resolve, 25);
      });
    }

    throw new Error("Clikker storefront core did not become ready.");
  }

  function getStore() {
    var api = StoreAPI();
    return typeof api.getStore === "function" ? api.getStore() : null;
  }

  function getCreator() {
    var api = StoreAPI();
    return typeof api.getCreator === "function" ? api.getCreator() : null;
  }

  function getSlug() {
    var api = StoreAPI();
    return typeof api.getSlug === "function" ? clean(api.getSlug()) : "";
  }

  function isPreview() {
    var api = StoreAPI();
    return typeof api.isPreview === "function" ? api.isPreview() === true : false;
  }

  function getProduct(productId) {
    var api = StoreAPI();
    var id = clean(productId);

    if (!id) return null;

    if (typeof api.getProductById === "function") {
      return api.getProductById(id);
    }

    if (typeof api.getProducts === "function") {
      return api.getProducts().find(function (product) {
        return clean(product && product.id) === id;
      }) || null;
    }

    return null;
  }

  function showToast(message, type) {
    var api = StoreAPI();

    if (typeof api.showToast === "function") {
      api.showToast(message, type);
      return;
    }

    console.info("[Clikker Checkout]", message);
  }

  /* ========================================================================
     SHARED UTILITY WRAPPERS
     ======================================================================== */

  function clean(value) {
    var fn = StoreUtils().clean;
    return typeof fn === "function"
      ? fn(value)
      : String(value == null ? "" : value).trim();
  }

  function firstValue() {
    var fn = StoreUtils().firstValue;

    if (typeof fn === "function") {
      return fn.apply(null, arguments);
    }

    for (var i = 0; i < arguments.length; i += 1) {
      var value = arguments[i];
      if (value !== undefined && value !== null && value !== "") return value;
    }

    return "";
  }

  function nested(object, path, fallback) {
    var fn = StoreUtils().nested;
    if (typeof fn === "function") return fn(object, path, fallback);

    try {
      var value = path.split(".").reduce(function (current, key) {
        return current && current[key] !== undefined ? current[key] : undefined;
      }, object);

      return value === undefined || value === null ? fallback : value;
    } catch (_) {
      return fallback;
    }
  }

  function toArray(value) {
    var fn = StoreUtils().toArray;
    return typeof fn === "function" ? fn(value) : (Array.isArray(value) ? value : []);
  }

  function boolValue(value, fallback) {
    var fn = StoreUtils().boolValue;
    if (typeof fn === "function") return fn(value, fallback);

    if (value === true || value === false) return value;
    if (value === "true") return true;
    if (value === "false") return false;
    return fallback;
  }

  function withTimeout(promise, timeoutMs, message) {
    var fn = StoreUtils().withTimeout;
    if (typeof fn === "function") return fn(promise, timeoutMs, message);

    var timer = null;
    var timeoutPromise = new Promise(function (_, reject) {
      timer = setTimeout(function () {
        reject(new Error(message || "The request took too long."));
      }, timeoutMs);
    });

    return Promise.race([promise, timeoutPromise]).finally(function () {
      if (timer) clearTimeout(timer);
    });
  }


  function esc(value) {
    var fn = StoreUtils().esc;
    if (typeof fn === "function") return fn(value);

    return String(value == null ? "" : value).replace(/[&<>"']/g, function (character) {
      return {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;"
      }[character];
    });
  }

  function safeUrl(value) {
    var fn = StoreUtils().safeUrl;
    if (typeof fn === "function") return fn(value);

    var text = clean(value);
    if (!text) return "";

    try {
      var url = new URL(text, window.location.origin);
      return ["https:", "http:"].indexOf(url.protocol) >= 0 ? url.href : "";
    } catch (_) {
      return "";
    }
  }

  function money(value, currency) {
    var fn = StoreUtils().money;
    if (typeof fn === "function") return fn(value, currency);

    try {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: currency || "INR",
        maximumFractionDigits: 0
      }).format(Number(value || 0));
    } catch (_) {
      return "₹" + Number(value || 0).toLocaleString("en-IN");
    }
  }

  function typeOf(product) {
    var fn = StoreUtils().typeOf;
    if (typeof fn === "function") return fn(product);

    return clean(firstValue(
      product && product.workflowType,
      product && product.workflow_type,
      product && product.productType,
      product && product.product_type,
      product && product.fulfilmentType,
      product && product.fulfillmentType,
      product && product.type,
      "custom_service"
    )).toLowerCase();
  }

  function typeLabel(type) {
    var fn = StoreUtils().typeLabel;
    if (typeof fn === "function") return fn(type);
    return clean(type).replace(/_/g, " ");
  }

  function workflowMeta(type) {
    var fn = StoreUtils().workflowMeta;
    if (typeof fn === "function") return fn(type);

    var engine = window.ClikkerProductEngine || null;
    return engine && typeof engine.getWorkflowMeta === "function"
      ? engine.getWorkflowMeta(type)
      : null;
  }

  function workflowDefinition(type) {
    var fn = StoreUtils().workflowDefinition;
    if (typeof fn === "function") return fn(type);

    var engine = window.ClikkerProductEngine || null;
    return engine && typeof engine.getWorkflow === "function"
      ? engine.getWorkflow(type)
      : null;
  }

  function productValue(product, key, fallback) {
    var fn = StoreUtils().productValue;
    if (typeof fn === "function") return fn(product, key, fallback);

    if (!product) return fallback;
    if (product[key] !== undefined && product[key] !== null && product[key] !== "") {
      return product[key];
    }

    var raw = product.raw || {};
    if (raw[key] !== undefined && raw[key] !== null && raw[key] !== "") {
      return raw[key];
    }

    return fallback;
  }

  function humanizeValue(value) {
    var fn = StoreUtils().humanizeValue;
    if (typeof fn === "function") return fn(value);

    return clean(value)
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, function (letter) {
        return letter.toUpperCase();
      });
  }

  /* ========================================================================
     PRODUCT PRESENTATION FALLBACKS
     ======================================================================== */

  var PRODUCT_FALLBACK_DESCRIPTIONS = {
    creator_reply: "Send your question or message and receive a personalised reply directly from the creator.",
    video_message: "Request a personalised video created especially for you or someone you care about.",
    scheduled_call: "Book dedicated one-to-one time with the creator for a focused conversation.",
    digital_download: "Get access to a creator-made digital product immediately after secure payment.",
    custom_service: "Request a personalised creator service tailored to your requirements.",
    social_promotion: "Collaborate with the creator on a social media promotion, campaign or brand activation.",
    content_access: "Unlock exclusive creator content after secure payment.",
    community_access: "Join a creator-led paid community and receive access after payment."
  };

  function productFallbackDescription(product) {
    return PRODUCT_FALLBACK_DESCRIPTIONS[typeOf(product)] ||
      PRODUCT_FALLBACK_DESCRIPTIONS.custom_service;
  }

  /* ========================================================================
     SECURE BACKEND CHECKOUT
     ======================================================================== */

  function safeReturnUrl() {
    try {
      var url = new URL(window.location.href);
      url.hash = "";
      return url.href;
    } catch (_) {
      return window.location.origin + window.location.pathname;
    }
  }

  async function createSecureCheckout(product, values) {
    var productId = clean(product && product.id);
    var currentStore = getStore();
    var storeSlug = clean(firstValue(
      currentStore && currentStore.slug,
      currentStore && currentStore.handle,
      getSlug()
    ));

    if (!productId) {
      throw new Error("This product is missing its product ID.");
    }

    if (!storeSlug) {
      throw new Error("This product is not connected to a valid creator store.");
    }

    /*
      SECURITY MODEL

      The public browser does NOT write to Firestore orders.

      It sends only identifiers + buyer requirements to the trusted backend.
      createClikkerCheckout must:
      1. Resolve the public store and product from Firestore.
      2. Verify product is published/public.
      3. Read the authoritative price/currency from Firestore.
      4. Create the pending order with Firebase Admin SDK.
      5. Create the Shopify checkout/draft order.
      6. Return checkoutUrl + clikkerOrderId.

      Price, ownerId, creatorId and payment amount are intentionally NOT
      accepted as authority from this browser request.
    */
    var response = await withTimeout(
      fetch(SHOPIFY_CHECKOUT_FUNCTION_URL, {
        method: "POST",
        mode: "cors",
        cache: "no-store",
        credentials: "omit",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          confirm: "CREATE_CLIKKER_CHECKOUT",
          productId: productId,
          storeSlug: storeSlug,
          buyerRequirements: values || {},
          returnUrl: safeReturnUrl(),
          client: {
            source: "creator_store_direct",
            storefrontVersion: clean(window.CLIKKER_PUBLIC_STORE_VERSION || ""),
            storeCoreVersion: clean(window.CLIKKER_STOREFRONT_CORE_JS_VERSION || ""),
            checkoutVersion: VERSION
          }
        })
      }),
      PAYMENT_TIMEOUT_MS,
      "Secure payment checkout took too long to prepare."
    );

    var result = null;

    try {
      result = await response.json();
    } catch (_) {
      result = null;
    }

    if (!response.ok || !result || result.success !== true) {
      throw new Error(clean(firstValue(
        result && result.message,
        result && result.details,
        result && result.error,
        "Shopify could not prepare secure payment."
      )));
    }

    var checkoutUrl = safeUrl(result.checkoutUrl);

    if (!checkoutUrl || !/^https:\/\//i.test(checkoutUrl)) {
      throw new Error("Shopify did not return a valid secure checkout link.");
    }

    var orderId = clean(firstValue(
      result.clikkerOrderId,
      result.orderId,
      result.id,
      ""
    ));

    /*
      Recovery information only. Firestore remains backend-owned.
      sessionStorage failures must never block checkout.
    */
    try {
      if (orderId) {
        sessionStorage.setItem("clikkerPendingOrderId", orderId);
      }

      sessionStorage.setItem(
        "clikkerShopifyDraftOrderId",
        clean(result.draftOrderId)
      );

      sessionStorage.setItem(
        "clikkerPendingProductId",
        productId
      );

      sessionStorage.setItem(
        "clikkerPendingProductType",
        typeOf(product)
      );

      sessionStorage.setItem(
        "clikkerPendingWorkflowType",
        typeOf(product)
      );
    } catch (_) {}

    return {
      checkoutUrl: checkoutUrl,
      orderId: orderId,
      draftOrderId: clean(result.draftOrderId),
      raw: result
    };
  }

  /* ========================================================================
     PRODUCT FACTS
     ======================================================================== */

  function fact(label, value) {
    if (value === undefined || value === null || value === "") return "";

    return '<div class="cps-fact"><span>' + esc(label) +
      '</span><strong>' + esc(value) + '</strong></div>';
  }

  function productFacts(product) {
    var type = typeOf(product);
    var facts = [];

    if (type === "creator_reply") {
      facts.push(fact(
        "Reply format",
        humanizeValue(productValue(
          product,
          "reply_format",
          productValue(product, "replyFormat", "Creator-selected")
        ))
      ));
      facts.push(fact(
        "Delivery",
        Number(productValue(product, "delivery_hours", productValue(product, "deliveryHours", 0))) > 0
          ? "Within " + Number(productValue(product, "delivery_hours", productValue(product, "deliveryHours", 0))) + " hours"
          : "As stated"
      ));
      facts.push(fact(
        "Daily availability",
        Number(productValue(product, "daily_limit", productValue(product, "dailyLimit", 0))) > 0
          ? Number(productValue(product, "daily_limit", productValue(product, "dailyLimit", 0))) + " orders"
          : "Limited"
      ));
      facts.push(fact(
        "Maximum reply",
        productValue(product, "maximum_reply_length", productValue(product, "maximumReplyLength", "Not specified"))
      ));
    } else if (type === "scheduled_call") {
      facts.push(fact(
        "Duration",
        productValue(product, "duration_minutes", productValue(product, "duration", ""))
          ? productValue(product, "duration_minutes", productValue(product, "duration", "")) + " minutes"
          : "Not specified"
      ));
      facts.push(fact("Platform", humanizeValue(productValue(product, "platform", "To be confirmed"))));
      facts.push(fact("Availability", productValue(product, "availability", "Creator availability")));
      facts.push(fact(
        "Rescheduling",
        boolValue(
          productValue(product, "rescheduling_allowed", productValue(product, "reschedulingAllowed", "")),
          true
        ) ? "Allowed" : "Not allowed"
      ));
    } else if (type === "video_message") {
      facts.push(fact(
        "Delivery",
        Number(productValue(product, "delivery_hours", productValue(product, "deliveryHours", 0))) > 0
          ? "Within " + Number(productValue(product, "delivery_hours", productValue(product, "deliveryHours", 0))) + " hours"
          : "As stated"
      ));
      facts.push(fact(
        "Video length",
        productValue(product, "maximum_video_minutes", productValue(product, "maximumVideoMinutes", ""))
          ? productValue(product, "maximum_video_minutes", productValue(product, "maximumVideoMinutes", "")) + " minutes"
          : "Not specified"
      ));
      facts.push(fact("Orientation", humanizeValue(productValue(product, "orientation", "Creator decides"))));
      facts.push(fact("Language", humanizeValue(productValue(product, "language", "As stated"))));
    } else if (type === "social_promotion") {
      facts.push(fact(
        "Promotion",
        humanizeValue(productValue(
          product,
          "promotion_platform",
          productValue(product, "promotionPlatform", "Social promotion")
        ))
      ));
      facts.push(fact(
        "Retention",
        productValue(product, "content_retention_hours", productValue(product, "storyDurationHours", ""))
          ? productValue(product, "content_retention_hours", productValue(product, "storyDurationHours", "")) + " hours"
          : "As stated"
      ));
      facts.push(fact(
        "Link allowed",
        boolValue(
          productValue(product, "link_allowed", productValue(product, "linkAllowed", "")),
          true
        ) ? "Yes" : "No"
      ));
      facts.push(fact(
        "Approval",
        boolValue(
          productValue(product, "approval_required", productValue(product, "approvalRequired", "")),
          true
        ) ? "Required" : "Not required"
      ));
    } else if (type === "digital_download") {
      facts.push(fact("Delivery", "Instant after payment"));
      facts.push(fact(
        "Access",
        humanizeValue(productValue(product, "access_duration", productValue(product, "accessDuration", "Lifetime")))
      ));
      facts.push(fact(
        "Version",
        productValue(product, "file_version", productValue(product, "fileVersion", "Latest"))
      ));
      facts.push(fact(
        "Repeat download",
        boolValue(
          productValue(product, "allow_repeat_download", productValue(product, "allowRepeatDownload", "")),
          true
        ) ? "Yes" : "No"
      ));
    } else if (type === "content_access") {
      facts.push(fact("Access", productValue(product, "accessDuration", "As stated")));
      facts.push(fact("Format", productValue(product, "contentFormat", "Mixed")));
      facts.push(fact("Release", productValue(product, "releaseMethod", "Immediate")));
      facts.push(fact("Updates", productValue(product, "updateFrequency", "Creator-defined")));
    } else if (type === "community_access") {
      facts.push(fact("Platform", productValue(product, "communityPlatform", "Private community")));
      facts.push(fact("Billing", productValue(product, "billingType", "As stated")));
      facts.push(fact("Access", productValue(product, "accessDuration", "As stated")));
      facts.push(fact("Joining", productValue(product, "joiningMethod", "Invite after payment")));
    } else {
      facts.push(fact(
        "Delivery",
        Number(productValue(product, "delivery_hours", productValue(product, "deliveryHours", 0))) > 0
          ? "Within " + Number(productValue(product, "delivery_hours", productValue(product, "deliveryHours", 0))) + " hours"
          : "As stated"
      ));
      facts.push(fact("Deliverable", productValue(product, "deliverable", "Personalized service")));
      facts.push(fact(
        "Revisions",
        productValue(product, "revision_limit", productValue(product, "revisionLimit", "Not specified"))
      ));
      facts.push(fact(
        "Pricing",
        humanizeValue(productValue(product, "pricing_mode", productValue(product, "pricingMode", "Fixed price")))
      ));
    }

    return facts.filter(Boolean).join("");
  }

  /* ========================================================================
     BUYER REQUIREMENTS
     ======================================================================== */

  function normalizeBuyerFields(product) {
    var fields = firstValue(
      product && product.buyerFields,
      product && product.buyer_fields,
      nested(product, "raw.buyerFields", null),
      nested(product, "raw.buyer_fields", null)
    );

    var type = typeOf(product);
    var workflow = workflowDefinition(type);
    var workflowFields = [];

    if (workflow) {
      workflowFields = firstValue(
        Array.isArray(workflow.buyer_fields) ? workflow.buyer_fields : null,
        Array.isArray(workflow.buyerFields) ? workflow.buyerFields : null,
        workflow.fields && Array.isArray(workflow.fields.buyer)
          ? workflow.fields.buyer
          : null,
        []
      );
    }

    var legacyDefaults = {
      creator_reply: [
        { key: "customerName", label: "Your name", type: "text", required: true },
        { key: "customerEmail", label: "Email address", type: "email", required: true },
        {
          key: "preferredReplyFormat",
          label: "Preferred reply format",
          type: "select",
          required: true,
          options: ["Text reply", "Voice note", "Video reply", "Creator can choose"]
        },
        { key: "question", label: "Your question or message", type: "textarea", required: true }
      ],
      video_message: [
        { key: "customerName", label: "Your name", type: "text", required: true },
        { key: "customerEmail", label: "Email address", type: "email", required: true },
        { key: "recipientName", label: "Recipient name", type: "text", required: true },
        { key: "occasion", label: "Occasion", type: "text", required: true },
        { key: "messageInstructions", label: "Message instructions", type: "textarea", required: true }
      ],
      scheduled_call: [
        { key: "customerName", label: "Your name", type: "text", required: true },
        { key: "customerEmail", label: "Email address", type: "email", required: true },
        { key: "customerPhone", label: "Phone number", type: "tel", required: true },
        { key: "callReason", label: "What would you like to discuss?", type: "textarea", required: true },
        { key: "preferredDate", label: "Preferred date", type: "date", required: true }
      ],
      digital_download: [
        { key: "customerName", label: "Your name", type: "text", required: true },
        { key: "customerEmail", label: "Email address", type: "email", required: true }
      ],
      custom_service: [
        { key: "customerName", label: "Your name", type: "text", required: true },
        { key: "customerEmail", label: "Email address", type: "email", required: true },
        { key: "requirements", label: "Tell the creator what you need", type: "textarea", required: true }
      ],
      social_promotion: [
        { key: "customerName", label: "Your name", type: "text", required: true },
        { key: "customerEmail", label: "Email address", type: "email", required: true },
        { key: "brandName", label: "Brand or account name", type: "text", required: true },
        { key: "brandURL", label: "Brand website or profile", type: "url", required: true },
        { key: "promotionDetails", label: "Promotion details", type: "textarea", required: true }
      ],
      content_access: [
        { key: "customerName", label: "Your name", type: "text", required: true },
        { key: "customerEmail", label: "Email address", type: "email", required: true }
      ],
      community_access: [
        { key: "customerName", label: "Your name", type: "text", required: true },
        { key: "customerEmail", label: "Email address", type: "email", required: true }
      ]
    };

    var source = Array.isArray(fields) && fields.length
      ? fields
      : (workflowFields.length
          ? workflowFields
          : (legacyDefaults[type] || legacyDefaults.custom_service));

    var normalized = source.map(function (field) {
      field = field || {};

      var fieldKey = clean(firstValue(field.key, field.name, field.id, "field"));
      var fieldType = clean(firstValue(
        field.type,
        field.input,
        field.input_type,
        "text"
      )).toLowerCase();

      var rawOptions = firstValue(
        Array.isArray(field.options) ? field.options : null,
        Array.isArray(field.choices) ? field.choices : null,
        Array.isArray(field.values) ? field.values : null,
        Array.isArray(field.items) ? field.items : null,
        []
      );

      var options = toArray(rawOptions).map(function (option) {
        if (typeof option === "object" && option) {
          return {
            value: clean(firstValue(option.value, option.key, option.id, option.label)),
            label: clean(firstValue(option.label, option.name, option.title, option.value, option.key)),
            disabled: option.disabled === true
          };
        }

        return {
          value: clean(option),
          label: clean(option),
          disabled: false
        };
      }).filter(function (option) {
        return option.value && option.label;
      });

      if (fieldType === "select" && !options.length) {
        var defaults = legacyDefaults[type] || legacyDefaults.custom_service;
        var fallbackField = defaults.find(function (item) {
          var defaultKey = clean(item && item.key).toLowerCase();
          var currentKey = fieldKey.toLowerCase();

          return defaultKey === currentKey ||
            (currentKey.indexOf("preferred") >= 0 && defaultKey.indexOf("preferred") >= 0);
        });

        options = toArray(fallbackField && fallbackField.options).map(function (option) {
          return {
            value: clean(option),
            label: clean(option),
            disabled: false
          };
        }).filter(function (option) {
          return option.value && option.label;
        });
      }

      return {
        key: fieldKey || "field",
        label: clean(firstValue(field.label, field.title, field.name, "Information")),
        type: fieldType,
        required: field.required === true,
        placeholder: clean(field.placeholder),
        helpText: clean(firstValue(field.helpText, field.help_text, field.description, "")),
        accept: clean(field.accept),
        options: options
      };
    });

    /* Every paid order must have a buyer email. */
    var hasEmail = normalized.some(function (field) {
      var key = clean(field.key).toLowerCase();
      return key === "customeremail" || field.type === "email";
    });

    if (!hasEmail) {
      normalized.splice(Math.min(1, normalized.length), 0, {
        key: "customerEmail",
        label: "Email address",
        type: "email",
        required: true,
        placeholder: "",
        helpText: "Used for payment confirmation and delivery.",
        accept: "",
        options: []
      });
    }

    return normalized;
  }

  function buyerField(field) {
    var key = clean(field.key || "field");
    var id = "buyer-" + key.replace(/[^a-zA-Z0-9_-]/g, "-");
    var required = field.required !== false;
    var label = clean(field.label || "Information");
    var placeholder = clean(field.placeholder || "");
    var helpText = clean(field.helpText || field.help_text || "");
    var type = clean(field.type || "text").toLowerCase();
    var requiredMark = required ? " *" : "";
    var requiredAttribute = required ? " required" : "";
    var common = ' id="' + esc(id) + '" data-buyer-key="' + esc(key) + '"' + requiredAttribute;

    if (type === "textarea") {
      return [
        '<div class="cps-field">',
          '<label for="' + esc(id) + '">' + esc(label + requiredMark) + '</label>',
          '<textarea class="cps-textarea"' + common + ' placeholder="' + esc(placeholder) + '"></textarea>',
          helpText ? '<small>' + esc(helpText) + '</small>' : "",
        '</div>'
      ].join("");
    }

    if (type === "select") {
      var options = toArray(field.options);

      if (!options.length && /preferred.*(reply.*)?format|reply.*format/i.test(key)) {
        options = [
          { value: "text_reply", label: "Text reply" },
          { value: "voice_note", label: "Voice note" },
          { value: "video_reply", label: "Video reply" },
          { value: "creator_can_choose", label: "Creator can choose" }
        ];
      }

      return [
        '<div class="cps-field">',
          '<label for="' + esc(id) + '">' + esc(label + requiredMark) + '</label>',
          '<select class="cps-select"' + common + '>',
            '<option value="">Select an option</option>',
            options.map(function (option) {
              var value = typeof option === "object"
                ? firstValue(option.value, option.label, "")
                : option;
              var optionLabel = typeof option === "object"
                ? firstValue(option.label, option.value, "")
                : option;
              var disabled = typeof option === "object" && option.disabled === true;

              return '<option value="' + esc(value) + '"' +
                (disabled ? ' disabled' : '') + '>' + esc(optionLabel) + '</option>';
            }).join(""),
          '</select>',
          helpText ? '<small>' + esc(helpText) + '</small>' : "",
        '</div>'
      ].join("");
    }

    if (type === "checkbox") {
      return [
        '<div class="cps-field">',
          '<label class="cps-checkbox" for="' + esc(id) + '">',
            '<input type="checkbox"' + common + '>',
            '<span>' + esc(label + requiredMark) + '</span>',
          '</label>',
          helpText ? '<small>' + esc(helpText) + '</small>' : "",
        '</div>'
      ].join("");
    }

    if (type === "file") {
      return [
        '<div class="cps-field">',
          '<label for="' + esc(id) + '">' + esc(label + requiredMark) + '</label>',
          '<input class="cps-input" type="file"' + common +
            (field.accept ? ' accept="' + esc(field.accept) + '"' : '') + '>',
          '<small>' + esc(helpText || "The selected file will be attached to your request when supported.") + '</small>',
        '</div>'
      ].join("");
    }

    var allowedInputTypes = ["text", "email", "tel", "url", "date", "number"];
    var inputType = allowedInputTypes.indexOf(type) >= 0 ? type : "text";

    return [
      '<div class="cps-field">',
        '<label for="' + esc(id) + '">' + esc(label + requiredMark) + '</label>',
        '<input class="cps-input" type="' + esc(inputType) + '"' + common +
          ' placeholder="' + esc(placeholder) + '">',
        helpText ? '<small>' + esc(helpText) + '</small>' : "",
      '</div>'
    ].join("");
  }

  function readBuyerField(input) {
    if (input.type === "checkbox") {
      return {
        value: input.checked,
        missing: input.required && !input.checked,
        invalid: false
      };
    }

    if (input.type === "file") {
      var file = input.files && input.files[0] ? input.files[0] : null;

      return {
        value: file
          ? {
              name: file.name,
              size: file.size,
              type: file.type || "",
              lastModified: file.lastModified || 0,
              pendingUpload: true,
              requiresCheckoutUpload: true
            }
          : null,
        missing: input.required && !file,
        invalid: false
      };
    }

    var value = clean(input.value);
    var missing = input.required && !value;
    var invalid = false;

    /* Use native browser validation for email/url/number/date when populated. */
    if (!missing && value && typeof input.checkValidity === "function") {
      invalid = !input.checkValidity();
    }

    return {
      value: value,
      missing: missing,
      invalid: invalid
    };
  }

  /* ========================================================================
     CHECKOUT FORM STATE
     ======================================================================== */

  function checkoutKey(product, values) {
    var currentStore = getStore();
    var currentCreator = getCreator();

    var email = clean(
      values.customerEmail ||
      values.email ||
      values.buyerEmail ||
      ""
    ).toLowerCase();

    return [
      clean(firstValue(
        currentStore && currentStore.id,
        product && product.storeId,
        product && product.store_id,
        ""
      )),
      clean(firstValue(
        currentCreator && currentCreator.id,
        product && product.creatorId,
        product && product.creator_id,
        ""
      )),
      clean(product && product.id),
      email
    ].join("|");
  }

  function lockCheckoutForm(form, button, message) {
    if (!form || !button) return;

    form.setAttribute("aria-busy", "true");

    form.querySelectorAll("input,select,textarea,button").forEach(function (node) {
      node.disabled = true;
    });

    button.classList.add("is-loading");
    button.setAttribute("aria-busy", "true");
    button.textContent = message || "Preparing secure payment…";
    modal.classList.add("is-checkout-loading");
  }

  function unlockCheckoutForm(form, button, originalText) {
    checkoutInProgress = false;
    activeCheckoutKey = "";

    if (form) {
      form.removeAttribute("aria-busy");
      form.querySelectorAll("input,select,textarea,button").forEach(function (node) {
        node.disabled = false;
      });
    }

    if (button) {
      button.classList.remove("is-loading");
      button.removeAttribute("aria-busy");
      button.textContent = originalText || "Pay securely";
    }

    modal.classList.remove("is-checkout-loading");
  }

  function showCheckoutSuccess(title, message) {
    var dialog = modal.querySelector(".cps-modal__dialog");
    if (!dialog) return;

    var overlay = dialog.querySelector("[data-checkout-overlay]");

    if (!overlay) {
      overlay = document.createElement("div");
      overlay.className = "cps-checkout-overlay";
      overlay.setAttribute("data-checkout-overlay", "");
      overlay.innerHTML =
        '<div class="cps-checkout-overlay__card" role="status" aria-live="polite">' +
          '<span class="cps-checkout-overlay__check">✓</span>' +
          '<strong data-checkout-title></strong>' +
          '<p data-checkout-message></p>' +
        '</div>';

      dialog.appendChild(overlay);
    }

    var titleNode = overlay.querySelector("[data-checkout-title]");
    var messageNode = overlay.querySelector("[data-checkout-message]");

    if (titleNode) titleNode.textContent = title;
    if (messageNode) messageNode.textContent = message;

    overlay.classList.add("is-visible");
  }

  /* ========================================================================
     PRODUCT MODAL
     ======================================================================== */

  function openProduct(productId) {
    var product = getProduct(productId);

    if (!product) {
      console.warn("[Clikker Checkout] Product not found", productId);
      showToast("This offer could not be opened. Please refresh and try again.", "warning");
      return;
    }

    if (!isPreview()) {
      var publicStatus = clean(product.status).toLowerCase();
      var publicVisibility = clean(product.visibility || "public").toLowerCase();

      if (publicStatus !== "published" || publicVisibility !== "public") {
        showToast("This offer is not currently available.", "warning");
        return;
      }
    }

    if (checkoutInProgress) {
      showToast("Secure payment is already being prepared.", "info");
      return;
    }

    activeProductId = clean(product.id);
    lastFocusedElement = document.activeElement;

    var title = clean(firstValue(product.title, product.name, "Product"));
    var type = typeOf(product);
    var buyerFields = normalizeBuyerFields(product);

    var instructions = clean(firstValue(
      product.customerInstructions,
      product.customer_instructions,
      productValue(
        product,
        "customer_instructions",
        productValue(product, "customerInstructions", "")
      )
    ));

    var refundPolicy = clean(firstValue(
      product.refundPolicy,
      product.refund_policy,
      productValue(
        product,
        "refund_policy",
        productValue(product, "refundPolicy", "")
      )
    ));

    var cancellationPolicy = clean(firstValue(
      product.cancellationPolicy,
      product.cancellation_policy,
      productValue(
        product,
        "cancellation_policy",
        productValue(product, "cancellationPolicy", "")
      )
    ));

    var reschedulingPolicy = clean(firstValue(
      product.reschedulingPolicy,
      product.rescheduling_policy,
      productValue(
        product,
        "rescheduling_policy",
        productValue(product, "reschedulingPolicy", "")
      )
    ));

    modal.className = "cps-modal is-open";
    modal.setAttribute("aria-hidden", "false");
    modal.setAttribute("data-product-id", activeProductId);

    modal.innerHTML = [
      '<div class="cps-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="cpsModalTitle">',
        '<div class="cps-modal__head">',
          '<div>',
            '<h2 id="cpsModalTitle">' + esc(title) + '</h2>',
            '<p>' + esc(typeLabel(type)) + '</p>',
          '</div>',
          '<button class="cps-close" type="button" data-close-product aria-label="Close product">×</button>',
        '</div>',
        '<div class="cps-modal__body">',
          '<div class="cps-product-detail">',
            '<div>',
              '<section class="cps-detail__section">',
                '<h3>About this offer</h3>',
                '<p>' + esc(firstValue(
                  product.description,
                  product.shortDescription,
                  product.short_description,
                  productFallbackDescription(product)
                )) + '</p>',
              '</section>',
              '<section class="cps-detail__section">',
                '<h3>What to expect</h3>',
                '<div class="cps-detail__facts">' + productFacts(product) + '</div>',
              '</section>',
              instructions
                ? '<section class="cps-detail__section"><h3>Instructions</h3><p>' + esc(instructions) + '</p></section>'
                : "",
              (reschedulingPolicy || cancellationPolicy)
                ? '<section class="cps-detail__section"><h3>Booking policy</h3><p>' + esc(
                    [reschedulingPolicy, cancellationPolicy].filter(Boolean).join("\n\n")
                  ) + '</p></section>'
                : "",
              refundPolicy
                ? '<section class="cps-detail__section"><h3>Refund policy</h3><p>' + esc(refundPolicy) + '</p></section>'
                : "",
            '</div>',
            '<aside class="cps-order-box">',
              '<div class="cps-order-box__price">',
                '<div><span>Total price</span><strong>' + esc(money(product.price, product.currency || "INR")) + '</strong></div>',
                '<span>' + esc(type === "digital_download" ? "Instant delivery" : "Creator fulfilled") + '</span>',
              '</div>',
              '<div id="cpsBuyerAlert" class="cps-alert" role="alert" aria-live="polite"></div>',
              '<form id="cpsBuyerForm" novalidate>',
                buyerFields.map(buyerField).join(""),
                '<button class="cps-btn cps-btn--primary cps-btn--wide" id="cpsContinueCheckout" type="submit">Pay ' +
                  esc(money(product.price, product.currency || "INR")) + ' securely</button>',
                '<p class="cps-form-note">You will go directly to Shopify and Razorpay secure payment. No second Clikker form.</p>',
              '</form>',
            '</aside>',
          '</div>',
        '</div>',
      '</div>'
    ].join("");

    document.body.classList.add("cps-modal-open");

    var closeButton = modal.querySelector("[data-close-product]");
    if (closeButton) closeButton.focus();

    modal.querySelectorAll("[data-close-product]").forEach(function (button) {
      button.onclick = closeProduct;
    });

    modal.onclick = function (event) {
      if (event.target === modal) closeProduct();
    };

    if (modalKeyHandler) {
      document.removeEventListener("keydown", modalKeyHandler);
      modalKeyHandler = null;
    }

    modalKeyHandler = function (event) {
      if (event.key !== "Tab") return;

      var focusable = modal.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", modalKeyHandler);

    var buyerForm = document.getElementById("cpsBuyerForm");

    if (!buyerForm) {
      console.error("[Clikker Checkout] Buyer form could not be rendered.");
      return;
    }

    buyerForm.onsubmit = async function (event) {
      event.preventDefault();

      if (checkoutInProgress) return;

      var form = event.currentTarget;
      var alertBox = document.getElementById("cpsBuyerAlert");
      var checkoutButton = document.getElementById("cpsContinueCheckout");
      var values = {};
      var hasError = false;
      var firstInvalid = null;
      var originalButtonText =
        "Pay " + money(product.price, product.currency || "INR") + " securely";

      modal.querySelectorAll("[data-buyer-key]").forEach(function (input) {
        var result = readBuyerField(input);
        var key = input.getAttribute("data-buyer-key");

        values[key] = result.value;
        input.removeAttribute("aria-invalid");

        if (result.missing || result.invalid) {
          hasError = true;
          input.setAttribute("aria-invalid", "true");
          if (!firstInvalid) firstInvalid = input;
        }
      });

      if (hasError) {
        if (alertBox) {
          alertBox.className = "cps-alert is-visible";
          alertBox.textContent =
            "Please complete all required details correctly before payment.";
        }

        if (firstInvalid && typeof firstInvalid.focus === "function") {
          firstInvalid.focus();
        }
        return;
      }

      var key = checkoutKey(product, values);
      if (checkoutInProgress || activeCheckoutKey === key) return;

      checkoutInProgress = true;
      activeCheckoutKey = key;
      lockCheckoutForm(form, checkoutButton, "Preparing secure payment…");

      if (alertBox) {
        alertBox.className = "cps-alert is-visible";
        alertBox.textContent =
          "Preparing your secure checkout. Please wait and do not click again.";
      }

      try {
        var checkout = await createSecureCheckout(product, values);

        if (alertBox) {
          alertBox.className = "cps-alert is-visible is-success";
          alertBox.textContent =
            "Request secured. Opening Shopify payment…";
        }

        showCheckoutSuccess(
          "Secure checkout is ready",
          "Opening Shopify and Razorpay payment now. Please do not close this page."
        );

        if (checkoutButton) checkoutButton.textContent = "Opening secure payment…";

        setTimeout(function () {
          window.location.assign(checkout.checkoutUrl);
        }, 650);
      } catch (error) {
        console.error("[Clikker Direct Checkout]", error);

        unlockCheckoutForm(form, checkoutButton, originalButtonText);

        if (alertBox) {
          alertBox.className = "cps-alert is-visible";
          alertBox.textContent = clean(error && error.message) ||
            "Payment could not be prepared. Please try again.";
        }
      }
    };
  }

  function closeProduct() {
    if (checkoutInProgress) {
      showToast("Secure payment is being prepared. Please wait.", "info");
      return;
    }

    modal.className = "cps-modal";
    modal.setAttribute("aria-hidden", "true");
    modal.removeAttribute("data-product-id");
    modal.innerHTML = "";
    document.body.classList.remove("cps-modal-open");

    if (modalKeyHandler) {
      document.removeEventListener("keydown", modalKeyHandler);
      modalKeyHandler = null;
    }

    if (lastFocusedElement && typeof lastFocusedElement.focus === "function") {
      lastFocusedElement.focus();
    }

    lastFocusedElement = null;
    activeProductId = "";
    checkoutInProgress = false;
    activeCheckoutKey = "";
  }

  /* ========================================================================
     PAGE RECOVERY
     ======================================================================== */

  function resetCheckoutUIAfterPageShow() {
    /*
      Browsers can restore a page from bfcache after the buyer presses Back.
      Make the modal/form usable again instead of leaving a disabled button.
    */
    checkoutInProgress = false;
    activeCheckoutKey = "";
    modal.classList.remove("is-checkout-loading");

    var form = document.getElementById("cpsBuyerForm");
    var checkoutButton = document.getElementById("cpsContinueCheckout");

    if (form) {
      form.removeAttribute("aria-busy");
      form.querySelectorAll("input,select,textarea,button").forEach(function (node) {
        node.disabled = false;
      });
    }

    if (!checkoutButton) return;

    checkoutButton.disabled = false;
    checkoutButton.classList.remove("is-loading");
    checkoutButton.removeAttribute("aria-busy");

    var product = getProduct(
      clean(modal.getAttribute("data-product-id")) || activeProductId
    );

    if (product) {
      checkoutButton.textContent =
        "Pay " + money(product.price, product.currency || "INR") + " securely";
      return;
    }

    checkoutButton.textContent = "Pay securely";
  }

  document.addEventListener("keydown", function (event) {
    if (event.key === "Escape" && modal.classList.contains("is-open")) {
      closeProduct();
    }
  });

  window.addEventListener("pageshow", resetCheckoutUIAfterPageShow);

  /* ========================================================================
     PUBLIC CHECKOUT API
     ======================================================================== */

  function publishCheckoutAPI() {
    CheckoutAPI.version = VERSION;
    CheckoutAPI.requiredStoreCoreVersion = REQUIRED_STORE_CORE_VERSION;
    CheckoutAPI.page = "public-store";

    CheckoutAPI.openProduct = openProduct;
    CheckoutAPI.closeProduct = closeProduct;

    CheckoutAPI.isOpen = function () {
      return modal.classList.contains("is-open");
    };

    CheckoutAPI.isCheckoutInProgress = function () {
      return checkoutInProgress;
    };

    CheckoutAPI.getActiveProductId = function () {
      return activeProductId;
    };

    CheckoutAPI.getState = function () {
      return {
        version: VERSION,
        activeProductId: activeProductId,
        checkoutInProgress: checkoutInProgress,
        activeCheckoutKey: activeCheckoutKey,
        modalOpen: modal.classList.contains("is-open")
      };
    };

    /* Useful for controlled debugging/testing; normal UI should use openProduct(). */
    CheckoutAPI.createSecureCheckout = createSecureCheckout;
  }

  async function init() {
    publishCheckoutAPI();

    try {
      var storeApi = await waitForStoreAPI();

      if (storeApi.version && clean(storeApi.version) !== REQUIRED_STORE_CORE_VERSION) {
        console.warn(
          "[Clikker Checkout] Store core version differs from tested version.",
          {
            expected: REQUIRED_STORE_CORE_VERSION,
            actual: storeApi.version
          }
        );
      }

      /* Rebind in case the store finished rendering before this asset executed. */
      if (typeof storeApi.bindProductButtons === "function") {
        storeApi.bindProductButtons();
      }

      window.dispatchEvent(new CustomEvent("clikker:checkout-ready", {
        detail: {
          version: VERSION,
          storeCoreVersion: clean(storeApi.version || "")
        }
      }));

      console.info("[Clikker Checkout] Ready", VERSION, {
        storeCoreVersion: clean(storeApi.version || ""),
        paymentProvider: "Shopify/Razorpay",
        orderAuthority: "backend"
      });
    } catch (error) {
      console.error("[Clikker Checkout] Initialization failed", error);
    }
  }

  init();
})();
